import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Session-48: Admin panel for customer-facing quick amount chips.
 * Deposit + Withdraw kept as separate lists with independent on/off toggles.
 * Each preset has a `label` and either an `amount` (numeric chip) or is
 * `text`-kind (like "Send QR" — no amount, sent as label only).
 */
const CustomAmountsSettings = ({ adminId }) => {
  const [state, setState] = useState({
    deposit_enabled: true, withdraw_enabled: false, support_enabled: false,
    deposit_presets: [], withdraw_presets: [], support_presets: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/settings/custom-amount-presets`);
      setState({
        deposit_enabled: !!r.data?.deposit_enabled,
        withdraw_enabled: !!r.data?.withdraw_enabled,
        support_enabled: !!r.data?.support_enabled,
        deposit_presets: r.data?.deposit_presets || [],
        withdraw_presets: r.data?.withdraw_presets || [],
        support_presets: r.data?.support_presets || [],
      });
    } catch (e) { toast.error('Failed to load presets'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Session-49: sanitize presets before shipping to the API — empty
      // strings in `amount` (text chips or blank rows) used to crash
      // Pydantic which then bubbled back a validation-array `detail` that
      // `toast.error()` tried to render as a React child.
      const _clean = (list, forceText) => (list || []).map(p => {
        const kind = forceText ? 'text' : (p.kind || 'amount');
        const amt = kind === 'text' || p.amount === '' || p.amount == null
          ? null
          : Number(p.amount);
        return { ...p, kind, amount: Number.isFinite(amt) ? amt : null };
      });
      const body = {
        ...state,
        deposit_presets: _clean(state.deposit_presets),
        withdraw_presets: _clean(state.withdraw_presets),
        support_presets: _clean(state.support_presets, true),
      };
      await axios.put(`${API}/settings/custom-amount-presets`, body, { params: { admin_id: adminId } });
      toast.success('✅ Saved — customers will see this instantly');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? (d[0]?.msg || 'Save failed')
        : (typeof d === 'string' ? d : (e?.message || 'Save failed'));
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  // Session-48g: extended to also handle support chips (text-only quick
  // options like "Deposit me problem" / "Withdraw me problem" / "ID problem"
  // that a customer taps to instantly create a support request).
  const _key = (side) => `${side}_presets`;
  const addPreset = (side) => {
    const key = _key(side);
    setState(s => ({ ...s, [key]: [...s[key], { label: '', amount: '', kind: side === 'support' ? 'text' : 'amount' }] }));
  };
  const removePreset = (side, idx) => {
    const key = _key(side);
    setState(s => ({ ...s, [key]: s[key].filter((_, i) => i !== idx) }));
  };
  const updatePreset = (side, idx, field, value) => {
    const key = _key(side);
    setState(s => ({
      ...s,
      [key]: s[key].map((p, i) => i === idx ? { ...p, [field]: value } : p),
    }));
  };

  const renderPresets = (side) => {
    const list = state[_key(side)];
    const enabledKey = `${side}_enabled`;
    const enabled = state[enabledKey];
    const isSupport = side === 'support';
    return (
      <div className="mb-6 p-3 border border-slate-200 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm capitalize">{side} chips</h3>
          <label className="inline-flex items-center gap-2 text-xs" data-testid={`custom-amounts-toggle-${side}`}>
            <input type="checkbox" checked={enabled}
              onChange={(e) => setState(s => ({ ...s, [enabledKey]: e.target.checked }))} />
            {enabled ? 'ON' : 'OFF'}
          </label>
        </div>
        {!enabled && (
          <p className="text-[11px] text-slate-500 mb-2">Currently hidden from customers.</p>
        )}
        <div className="space-y-2">
          {list.map((p, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`custom-amounts-row-${side}-${idx}`}>
              <input type="text" value={p.label}
                onChange={(e) => updatePreset(side, idx, 'label', e.target.value)}
                placeholder={isSupport ? "Label (e.g. Deposit me problem)" : "Label (e.g. up to 500 / Send QR)"}
                className={`px-2 py-1.5 border border-slate-300 rounded text-xs ${isSupport ? 'col-span-11' : 'col-span-5'}`} />
              {!isSupport && (
                <>
                  <select value={p.kind || 'amount'}
                    onChange={(e) => updatePreset(side, idx, 'kind', e.target.value)}
                    className="col-span-3 px-2 py-1.5 border border-slate-300 rounded text-xs">
                    <option value="amount">Amount</option>
                    <option value="text">Text</option>
                  </select>
                  <input type="number" value={p.amount || ''}
                    disabled={p.kind === 'text'}
                    onChange={(e) => updatePreset(side, idx, 'amount', e.target.value)}
                    placeholder="₹"
                    className="col-span-3 px-2 py-1.5 border border-slate-300 rounded text-xs disabled:bg-slate-100" />
                </>
              )}
              <button onClick={() => removePreset(side, idx)}
                className="col-span-1 text-red-600 hover:text-red-800 text-lg"
                data-testid={`custom-amounts-remove-${side}-${idx}`}>×</button>
            </div>
          ))}
        </div>
        <button onClick={() => addPreset(side)}
          className="mt-3 w-full py-1.5 rounded border-2 border-dashed border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
          data-testid={`custom-amounts-add-${side}`}>
          + Add {side} chip
        </button>
      </div>
    );
  };

  if (loading) return <div className="p-4 text-center text-slate-400">Loading…</div>;

  return (
    <div className="p-4 max-w-3xl">
      <h2 className="font-bold text-lg mb-1">💵 Custom Quick-Reply Chips</h2>
      <p className="text-xs text-slate-500 mb-4">
        Yeh chips customer ke Raise Form modal me dikhengi. Click karte hi
        instant form send ho jaayegi. Support chips = text-only (e.g., "Deposit me problem").
      </p>
      {renderPresets('deposit')}
      {renderPresets('withdraw')}
      {renderPresets('support')}
      <button onClick={save} disabled={saving}
        className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50"
        data-testid="custom-amounts-save">
        {saving ? 'Saving…' : '💾 Save Changes'}
      </button>
    </div>
  );
};

export default CustomAmountsSettings;
